import { gql } from '@apollo/client';

import { EXCEPTION_MAPPINGS_FIELD } from '@/graphql/fragments/exception_mappings';
import { EXCEPTION_FIELDS } from '@/graphql/queries/db_exceptions';
import { ITEM_FIELDS } from '@/graphql/queries/items';
import { ORDER_FIELDS } from '@/graphql/queries/orders';
import { WAREHOUSE_EVENT_FIELDS } from '@/graphql/queries/order_wh_events';
import { CLIENT_FIELDS } from '@/graphql/queries/clients';
import { JOB_FIELDS } from '@/graphql/queries/jobs';
import { LOCATION_FIELDS } from '@/graphql/queries/locations';

export const GET_SHIPPERS = gql`
    ${CLIENT_FIELDS}

    query GetShippers($where: orders_bool_exp!) {
        results: orders(distinct_on: [shipper_id], where: $where) {
            order_id
            shipper_id
            order_shipper {
                ...ClientFields
            }
        }
    }
`;

export const GET_CARRIERS = gql`
    ${CLIENT_FIELDS}

    query GetCarriers($where: orders_bool_exp!) {
        results: orders(distinct_on: [carrier_id], where: $where) {
            order_id
            carrier_id
            order_carrier {
                ...ClientFields
            }
        }
    }
`;

export const GET_EXCEPTIONS = gql`
    ${EXCEPTION_MAPPINGS_FIELD}
    ${EXCEPTION_FIELDS}
    ${ITEM_FIELDS}
    ${ORDER_FIELDS}
    ${WAREHOUSE_EVENT_FIELDS}
    ${CLIENT_FIELDS}
    ${LOCATION_FIELDS}

    query GetExceptions(
        $where: exception_mappings_bool_exp!
        $order_by: [exception_mappings_order_by!]!
        $offset: Int = 0
        $limit: Int = 100
    ) {
        results: exception_mappings(where: $where, order_by: $order_by, offset: $offset, limit: $limit) {
            ...ExceptionMappingsFields

            order {
                ...OrderFields

                order_shipper {
                    ...ClientFields
                    locations {
                        ...LocationFields
                    }
                }

                order_carrier {
                    ...ClientFields
                    locations {
                        ...LocationFields
                    }
                }

                itemsByOrderId {
                    ...ItemFields
                }
            }

            item {
                ...ItemFields
            }

            exception {
                ...ExceptionFields
            }
        }
    }
`;

export const RESOLVE_EXCEPTION = gql`
    ${ORDER_FIELDS}
    ${WAREHOUSE_EVENT_FIELDS}
    ${ITEM_FIELDS}
    ${CLIENT_FIELDS}

    mutation ResolveException(
        $exceptions: [uuid!]
        $events: [uuid!]
        $orders: [uuid!]
        $items: [uuid!]
        $order_update: orders_set_input = {}
        $event_update: order_wh_events_set_input = {}
        $exception_update: exceptions_set_input = {}
        $route_id: uuid
        $route_update: routes_set_input = {}
        $include_route_update: Boolean!
    ) {
        event: update_order_wh_events(where: { event_id: { _in: $events } }, _set: $event_update) {
            affected_rows
        }

        exception: update_exceptions(where: { exception_id: { _in: $exceptions } }, _set: $exception_update) {
            affected_rows
            returning {
                exception_id
                status
            }
        }

        items_removed: update_items(where: { item_id: { _in: $items } }, _set: { order_id: null }) {
            affected_rows
            returning {
                item_id
            }
        }

        order: update_orders(where: { order_id: { _in: $orders } }, _set: $order_update) {
            affected_rows
            returning {
                ...OrderFields

                order_shipper {
                    ...ClientFields
                }

                order_carrier {
                    ...ClientFields
                }

                itemsByOrderId {
                    ...ItemFields
                }
            }
        }
        route: update_routes(where: { route_id: { _eq: $route_id } }, _set: $route_update)
            @include(if: $include_route_update) {
            affected_rows
            returning {
                route_id
                route_number
                estimated_driving_distance
                estimated_driving_time
            }
        }
    }
`;

export const GET_RETURN_ORDER = gql`
    ${ORDER_FIELDS}

    query GetReturnOrder($order_id: uuid!) {
        orders(where: { order_id: { _eq: $order_id } }) {
            ...OrderFields
        }
    }
`;

export const GET_JOB_INFO = gql`
    ${ORDER_FIELDS}
    ${JOB_FIELDS}
    ${ITEM_FIELDS}

    query GetJobInfo($job_id: uuid!) {
        jobs_by_pk(job_id: $job_id) {
            ...JobFields
            orders {
                ...OrderFields
                itemsByOrderId {
                    ...ItemFields
                }
            }
        }
    }
`;

/** An order's failed attempt-billing clones, never its split children. Sets the next -A{n} and finds an unpriced clone to reuse. */
export const ATTEMPT_CLONES_BY_ORIGINAL = gql`
    query AttemptClonesByOriginal($original_order_id: uuid!) {
        orders(
            where: {
                original_order_id: { _eq: $original_order_id }
                order_status: { _eq: "failed" }
                split_seq: { _is_null: true }
            }
        ) {
            order_id
            order_number
            # null until the clone is priced — lets a retry reuse an unpriced clone instead of inserting another
            price_breakdown
            # the attempt the clone bills (its only billable attempt)
            wh_events(
                where: { billable: { _eq: true }, action: { _like: "%ATTEMPT%" } }
                order_by: [{ created_at: asc_nulls_first }, { transition: { current: asc } }, { event_id: asc }]
            ) {
                event_id
                created_at
            }
        }
    }
`;

// Single-mutation insert of the "failed" billing clone
export const CLONE_FAILED_ORDER = gql`
    mutation CloneFailedOrder($order: orders_insert_input!) {
        order: insert_orders_one(object: $order) {
            order_id
            po_number
            order_status
        }
    }
`;
